import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Shield, ArrowLeft, FileText, Clock, Download, 
  CheckCircle, Timer, ChevronRight, AlertTriangle,
  ExternalLink, MessageSquare, PartyPopper, Sparkles, PenLine,
  FastForward
} from 'lucide-react';
import axios from 'axios';
import AppFooter from '../components/AppFooter';
import ResponseReceivedDialog from '../components/ResponseReceivedDialog';
import RateUsCard from '../components/RateUsCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DisputesPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfpbGuide, setCfpbGuide] = useState(null);
  const [showCfpb, setShowCfpb] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const [commentingOn, setCommentingOn] = useState(null);
  const [consumerComment, setConsumerComment] = useState('');
  const [responseDialogFor, setResponseDialogFor] = useState(null);

  useEffect(() => {
    fetchDisputes();
  }, []);

  const fetchDisputes = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/disputes`, {
        withCredentials: true
      });
      setDisputes(data);
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCfpbGuide = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/cfpb-guide`, { withCredentials: true });
      setCfpbGuide(data);
      setShowCfpb(true);
    } catch (e) {
      console.error('Failed to load CFPB guide:', e);
    }
  };

  const updateDisputeStatus = async (disputeId, status, notes) => {
    setUpdatingStatus(disputeId);
    try {
      await axios.put(`${API_URL}/api/dispute/${disputeId}/status`, {
        status,
        notes,
        response_received: status === 'response_received',
        escalate_cfpb: status === 'cfpb_escalated'
      }, { withCredentials: true });
      fetchDisputes();
    } catch (e) {
      console.error('Failed to update status:', e);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const resolveDispute = async (disputeId) => {
    setUpdatingStatus(disputeId);
    try {
      const { data } = await axios.post(`${API_URL}/api/dispute/${disputeId}/resolve`, {});
      setCelebration(data);
      setTimeout(() => setCelebration(null), 8000);
      fetchDisputes();
    } catch (e) {
      console.error('Failed to resolve dispute:', e);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const advanceClock = async (disputeId, days = 7) => {
    setUpdatingStatus(disputeId);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/dispute/${disputeId}/advance-clock?days=${days}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Clock advanced ${days} day${days === 1 ? '' : 's'}`, {
        description: `${data.days_remaining} day${data.days_remaining === 1 ? '' : 's'} remaining`,
      });
      fetchDisputes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not advance the clock');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const submitConsumerComment = async (disputeId) => {
    if (!consumerComment.trim()) return;
    setUpdatingStatus(disputeId);
    try {
      await axios.put(`${API_URL}/api/dispute/${disputeId}/status`, {
        status: 'not_satisfied',
        notes: consumerComment.trim()
      });
      toast.success('Your comments have been recorded');
      setCommentingOn(null);
      setConsumerComment('');
      fetchDisputes();
    } catch (e) {
      toast.error('Failed to save comments');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDownload = async (disputeId, bureau) => {
    const token = localStorage.getItem('selah_access_token');
    window.location.href = `${API_URL}/api/dispute/${disputeId}/letter?token=${token}`;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCardClick = (dispute) => {
    const s = dispute.status;
    if (s === 'mailed' || s === 'response_received' || s === 'not_satisfied') {
      setResponseDialogFor(dispute);
    } else if (s === 'generated') {
      navigate('/mail-hub', {
        state: { dispute: { dispute_id: dispute.dispute_id, bureau: dispute.bureau } },
      });
    }
    // resolved / cfpb_escalated → no click action (purely informational)
  };

  const isCardClickable = (dispute) =>
    ['mailed', 'response_received', 'not_satisfied', 'generated'].includes(dispute.status);

  const getStatusBadge = (status, daysRemaining) => {
    if (status === 'resolved') {
      return (
        <span className="inline-flex items-center px-2 py-1 bg-[#4CAF50]/10 text-[#4CAF50] text-xs font-['Manrope'] rounded">
          <CheckCircle className="w-3 h-3 mr-1" strokeWidth={1.5} />
          Resolved
        </span>
      );
    }
    if (status === 'mailed') {
      return (
        <span className="inline-flex items-center px-2 py-1 bg-[#4CAF50]/10 text-[#4CAF50] text-xs font-['Manrope'] rounded">
          <Timer className="w-3 h-3 mr-1" strokeWidth={1.5} />
          {daysRemaining} days left
        </span>
      );
    }
    if (status === 'response_received') {
      return (
        <span className="inline-flex items-center px-2 py-1 bg-[#E0BFB8]/30 text-[#B76E79] text-xs font-['Manrope'] rounded">
          <MessageSquare className="w-3 h-3 mr-1" strokeWidth={1.5} />
          Response Received
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 bg-[#E0BFB8]/30 text-[#B76E79] text-xs font-['Manrope'] rounded">
        <Clock className="w-3 h-3 mr-1" strokeWidth={1.5} />
        Pending
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="disputes-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </div>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-[#36454F]/70 hover:text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope']"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link 
          to="/dashboard" 
          className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Back to Dashboard
        </Link>

        {/* Page Title */}
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#36454F]">
            Dispute History
          </h1>
          <p className="mt-2 text-[#36454F]/65 font-['Manrope']">
            Track all your credit dispute letters and their status
          </p>
        </div>

        {/* Celebration Banner */}
        {celebration && (
          <div className="mb-6 p-6 bg-[#4CAF50]/10 border-2 border-[#4CAF50]/40 rounded-sm animate-fade-in-up text-center" data-testid="celebration-banner">
            <Sparkles className="w-10 h-10 text-[#4CAF50] mx-auto mb-3" strokeWidth={1.5} />
            <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-2">
              Your Slate is Clearing!
            </h2>
            <p className="text-[#36454F]/70 font-['Manrope']">
              {celebration.items_resolved} item(s) resolved with {celebration.bureau}. Keep going!
            </p>
            <p className="mt-3 font-['Playfair_Display'] text-sm italic text-[#36454F]/60">
              "See, I am doing a new thing! Now it springs up; do you not perceive it?" — Isaiah 43:19
            </p>
          </div>
        )}

        {/* Review prompt — shown at the happiest moment (an item just got resolved) */}
        {celebration && <RateUsCard />}

        {/* Disputes List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#36454F]/65 font-['Manrope']">Loading disputes...</p>
          </div>
        ) : disputes.length === 0 ? (
          <Card className="border-[#36454F]/15 bg-white animate-fade-in-up">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-[#36454F]/20 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-2">
                No disputes yet
              </h3>
              <p className="text-[#36454F]/65 font-['Manrope'] mb-6 max-w-md mx-auto">
                Upload your credit report to identify errors and generate your first dispute letter.
              </p>
              <Link to="/upload">
                <Button 
                  className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
                  data-testid="start-upload-btn"
                >
                  Upload Credit Report
                  <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {disputes.map((dispute, index) => {
              const clickable = isCardClickable(dispute);
              return (
              <Card 
                key={dispute.dispute_id} 
                className={`border-[#36454F]/15 bg-white animate-fade-in-up card-hover transition-all ${
                  clickable ? 'cursor-pointer hover:border-[#B76E79]/50 hover:shadow-md' : ''
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`dispute-card-${dispute.dispute_id}`}
                onClick={clickable ? () => handleCardClick(dispute) : undefined}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={clickable ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick(dispute);
                  }
                } : undefined}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] capitalize">
                          {dispute.bureau}
                        </h3>
                        {getStatusBadge(dispute.status, dispute.days_remaining)}
                      </div>
                      <p className="text-sm text-[#36454F]/50 font-['Manrope'] mb-3">
                        Created {new Date(dispute.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {dispute.items?.slice(0, 3).map((item, i) => (
                          <span 
                            key={i}
                            className="text-xs px-2 py-1 bg-[#F5F5F5] text-[#36454F]/70 rounded font-['Manrope']"
                          >
                            {item.account_name}
                          </span>
                        ))}
                        {dispute.items?.length > 3 && (
                          <span className="text-xs px-2 py-1 bg-[#F5F5F5] text-[#36454F]/50 rounded font-['Manrope']">
                            +{dispute.items.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end space-y-2 ml-4" onClick={(e) => e.stopPropagation()}>
                      {dispute.status === 'mailed' && (
                        <div className="text-right">
                          <div className="relative w-14 h-14">
                            <svg className="w-14 h-14 transform -rotate-90">
                              <circle
                                cx="28"
                                cy="28"
                                r="24"
                                fill="none"
                                stroke="rgba(54, 69, 79, 0.1)"
                                strokeWidth="4"
                              />
                              <circle
                                cx="28"
                                cy="28"
                                r="24"
                                fill="none"
                                stroke="#B76E79"
                                strokeWidth="4"
                                strokeDasharray={`${(dispute.days_remaining / 35) * 151} 151`}
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="font-['Manrope'] font-semibold text-sm text-[#36454F]">
                                {dispute.days_remaining}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(dispute.dispute_id, dispute.bureau)}
                        className="border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white font-['Manrope'] rounded-sm"
                        data-testid={`download-btn-${dispute.dispute_id}`}
                      >
                        <Download className="w-4 h-4 mr-1" strokeWidth={1.5} />
                        Download
                      </Button>
                    </div>
                  </div>

                  {dispute.status === 'generated' && (
                    <div className="mt-4 pt-4 border-t border-[#36454F]/10">
                      <Link 
                        to="/mail-hub" 
                        state={{ dispute: { dispute_id: dispute.dispute_id, bureau: dispute.bureau } }}
                      >
                        <Button
                          className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
                          data-testid={`mail-btn-${dispute.dispute_id}`}
                        >
                          Ready to Mail
                          <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                        </Button>
                      </Link>
                    </div>
                  )}

                  {/* Status Update Actions for Mailed Disputes */}
                  {dispute.status === 'mailed' && (
                    <div className="mt-4 pt-4 border-t border-[#36454F]/10" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => setResponseDialogFor(dispute)}
                          disabled={updatingStatus === dispute.dispute_id}
                          className="bg-[#4CAF50] hover:bg-[#4CAF50]/90 text-white font-['Manrope'] rounded-sm text-xs"
                          data-testid={`response-received-btn-${dispute.dispute_id}`}
                        >
                          <MessageSquare className="w-3 h-3 mr-1" strokeWidth={1.5} />
                          Response Received
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => resolveDispute(dispute.dispute_id)}
                          disabled={updatingStatus === dispute.dispute_id}
                          className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-xs"
                          data-testid={`resolve-btn-${dispute.dispute_id}`}
                        >
                          <Sparkles className="w-3 h-3 mr-1" strokeWidth={1.5} />
                          Dispute Resolved
                        </Button>
                        {dispute.days_remaining <= 0 && !dispute.cfpb_escalated && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              updateDisputeStatus(dispute.dispute_id, 'cfpb_escalated', 'Bureau failed to respond within 35 days');
                              loadCfpbGuide();
                            }}
                            disabled={updatingStatus === dispute.dispute_id}
                            className="border-[#D9534F] text-[#D9534F] hover:bg-[#D9534F] hover:text-white font-['Manrope'] rounded-sm text-xs"
                            data-testid={`escalate-cfpb-btn-${dispute.dispute_id}`}
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" strokeWidth={1.5} />
                            Escalate to CFPB
                          </Button>
                        )}
                      </div>
                      {dispute.cfpb_escalated && (
                        <span className="mt-2 inline-flex items-center text-xs text-[#D9534F] font-['Manrope']">
                          <AlertTriangle className="w-3 h-3 mr-1" strokeWidth={1.5} />
                          Escalated to CFPB
                        </span>
                      )}

                      {/* Test Utility: Wind the clock down */}
                      <div className="mt-3 pt-3 border-t border-dashed border-[#36454F]/10">
                        <p className="text-[10px] uppercase tracking-wider text-[#36454F]/40 font-['Manrope'] mb-1.5">
                          Testing — simulate time passing
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => advanceClock(dispute.dispute_id, 7)}
                            disabled={updatingStatus === dispute.dispute_id || dispute.days_remaining <= 0}
                            className="border-[#36454F]/20 text-[#36454F]/70 hover:bg-[#36454F]/5 font-['Manrope'] rounded-sm text-xs h-7 px-2"
                            data-testid={`advance-7d-btn-${dispute.dispute_id}`}
                          >
                            <FastForward className="w-3 h-3 mr-1" strokeWidth={1.5} />
                            +7 days
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => advanceClock(dispute.dispute_id, 35)}
                            disabled={updatingStatus === dispute.dispute_id || dispute.days_remaining <= 0}
                            className="border-[#36454F]/20 text-[#36454F]/70 hover:bg-[#36454F]/5 font-['Manrope'] rounded-sm text-xs h-7 px-2"
                            data-testid={`advance-35d-btn-${dispute.dispute_id}`}
                          >
                            <FastForward className="w-3 h-3 mr-1" strokeWidth={1.5} />
                            Skip to day 35
                          </Button>
                        </div>
                        <p className="text-[10px] text-[#36454F]/40 font-['Manrope'] mt-1">
                          Moves the mailed date back so you can preview the CFPB escalation and resolution flows.
                        </p>
                      </div>

                      {/* Not Satisfied / Consumer Comments */}
                      <div className="mt-3">
                        {commentingOn !== dispute.dispute_id ? (
                          <button
                            onClick={() => setCommentingOn(dispute.dispute_id)}
                            className="text-xs text-[#36454F]/40 hover:text-[#B76E79] font-['Manrope'] flex items-center transition-colors"
                            data-testid={`add-comment-btn-${dispute.dispute_id}`}
                          >
                            <PenLine className="w-3 h-3 mr-1" strokeWidth={1.5} />
                            Not satisfied? Add comments
                          </button>
                        ) : (
                          <div className="mt-2 p-3 bg-[#F5F5F5] rounded-sm space-y-2 animate-fade-in-up" data-testid={`comment-form-${dispute.dispute_id}`}>
                            <p className="text-xs font-['Manrope'] font-medium text-[#36454F]">Consumer Comments</p>
                            <Textarea
                              value={consumerComment}
                              onChange={(e) => setConsumerComment(e.target.value)}
                              placeholder="Describe why the dispute resolution was not satisfactory..."
                              className="border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope'] text-sm min-h-[80px]"
                              data-testid={`consumer-comment-${dispute.dispute_id}`}
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => submitConsumerComment(dispute.dispute_id)}
                                disabled={!consumerComment.trim() || updatingStatus === dispute.dispute_id}
                                className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-xs"
                                data-testid={`submit-comment-${dispute.dispute_id}`}
                              >
                                Submit
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setCommentingOn(null); setConsumerComment(''); }} className="text-[#36454F]/50 font-['Manrope'] text-xs">
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}

        {/* Create New Dispute */}
        {disputes.length > 0 && (
          <div className="mt-8 flex flex-wrap justify-center gap-3 animate-fade-in-up" style={{ animationDelay: `${disputes.length * 100}ms` }}>
            <Link to="/upload">
              <Button 
                variant="outline"
                className="border-[#36454F]/30 text-[#36454F] hover:bg-[#36454F] hover:text-white font-['Manrope'] rounded-sm"
                data-testid="new-dispute-btn"
              >
                <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Create New Dispute
              </Button>
            </Link>
            <Button 
              variant="outline"
              onClick={loadCfpbGuide}
              className="border-[#D9534F]/50 text-[#D9534F] hover:bg-[#D9534F] hover:text-white font-['Manrope'] rounded-sm"
              data-testid="cfpb-guide-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" strokeWidth={1.5} />
              CFPB Escalation Guide
            </Button>
          </div>
        )}

        {/* CFPB Guide Modal */}
        {showCfpb && cfpbGuide && (
          <Card className="mt-8 border-[#D9534F]/30 bg-white animate-fade-in-up" data-testid="cfpb-guide-section">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-['Playfair_Display'] text-xl text-[#36454F] flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-[#D9534F]" strokeWidth={1.5} />
                  {cfpbGuide.title}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowCfpb(false)} className="text-[#36454F]/50">
                  Close
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[#36454F]/80 font-['Manrope']">{cfpbGuide.description}</p>
              
              <div className="space-y-3">
                {cfpbGuide.steps?.map((step) => (
                  <div key={step.step} className="flex items-start space-x-3 p-3 bg-[#F5F5F5] rounded-sm">
                    <div className="w-6 h-6 rounded-full bg-[#D9534F] text-white flex items-center justify-center font-['Manrope'] text-xs font-semibold flex-shrink-0">
                      {step.step}
                    </div>
                    <div>
                      <p className="font-['Manrope'] font-medium text-[#36454F] text-sm">{step.title}</p>
                      <p className="text-xs text-[#36454F]/60 font-['Manrope']">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <a href={cfpbGuide.url} target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-[#D9534F] hover:bg-[#D9534F]/90 text-white font-['Manrope'] rounded-sm" data-testid="cfpb-file-complaint-btn">
                  <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  File a Complaint at CFPB
                </Button>
              </a>

              <div className="p-4 bg-[#E0BFB8]/10 border border-[#E0BFB8]/30 rounded-sm">
                <p className="font-['Playfair_Display'] text-sm italic text-[#36454F]">
                  "{cfpbGuide.verse}"
                </p>
                <p className="text-xs text-[#36454F]/50 font-['Manrope'] mt-2 italic">
                  {cfpbGuide.prayer}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      <AppFooter />
      {responseDialogFor && (
        <ResponseReceivedDialog
          open={!!responseDialogFor}
          onOpenChange={(open) => !open && setResponseDialogFor(null)}
          dispute={responseDialogFor}
          onComplete={fetchDisputes}
          onResolved={(data) => {
            setCelebration({
              items_resolved: data.items?.length || 1,
              bureau: data.bureau,
            });
            setTimeout(() => setCelebration(null), 8000);
          }}
        />
      )}
    </div>
  );
}
